import React, { useCallback, useEffect, useState } from 'react';
import { Box, VStack, HStack, Text, Button, IconButton, useToast, Alert, AlertIcon } from '@chakra-ui/react';
import { ExternalLinkIcon, DownloadIcon, DeleteIcon } from '@chakra-ui/icons';
import { useDropzone } from 'react-dropzone';

const FileUploader = ({ userId }) => {
  const [files, setFiles] = useState([]);
  const toast = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const onDrop = useCallback(acceptedFiles => {
    const formData = new FormData();
    acceptedFiles.forEach(file => {
      formData.append('files', file);
    });

    fetch(`${backendUrl}/uploads/${userId}`, {
      method: 'POST',
      body: formData,
    })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then(data => {
      fetchFiles(); // Re-fetch files list to update the UI
      toast({
        title: 'Upload successful',
        description: 'Files have been successfully uploaded.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    })
    .catch(error => {
      console.error('Error uploading files:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an issue uploading your files.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    });
  }, [userId, toast, backendUrl]);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const fetchFiles = useCallback(() => {
    fetch(`${backendUrl}/uploads/${userId}`)
      .then(response => response.json())
      .then(data => {
        setFiles(data.files || []); // Handle case when there are no files
      })
      .catch(error => {
        console.error('Error fetching files:', error);
        toast({
          title: 'Error fetching files',
          description: 'Could not fetch files.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      });
  }, [userId, backendUrl]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const deleteFile = useCallback((fileName,evt) => {
    evt.stopPropagation();
    fetch(`${backendUrl}/uploads/${userId}/${fileName}`, {
        method: 'DELETE',
    })
    .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        fetchFiles();  // Re-fetch files list to update the UI
        toast({
            title: 'File deleted',
            description: 'The file has been successfully deleted.',
            status: 'success',
            duration: 5000,
            isClosable: true,
        });
    })
    .catch(error => {
        console.error('Error deleting file:', error);
        toast({
            title: 'Deletion failed',
            description: 'There was an issue deleting the file.',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    });
}, [userId, toast, backendUrl, fetchFiles]);

  return (
    <Box
      {...getRootProps()}
      p={5}
      border="2px dashed"
      borderColor="gray.300"
      borderRadius="md"
      bg="white"
      boxShadow="sm"
      _hover={{ borderColor: 'blue.300', boxShadow: 'md' }}
      transition="all 0.3s"
    >
      <input {...getInputProps()} />
      <Text textAlign="center" my={2}>Drag 'n' drop files here, or click to select files</Text>
      <VStack spacing={4}>
        {files.length === 0 ? (
          <Alert status="info">
            <AlertIcon />
            No files uploaded. Start by dropping some files here, or click to select.
          </Alert>
        ) : (
          files.map((file, index) => (
            <HStack key={index} width="100%" p={2} _hover={{ bg: 'gray.100' }}>
              <Text flex="1" isTruncated>{file}</Text>
              {/*}
              <Button leftIcon={<ExternalLinkIcon />} onClick={() => window.open(`${backendUrl}/gpt_researcher/local_files/${userId}/${file}`, '_blank')} variant="outline" size="sm">
                View
              </Button>
              <Button leftIcon={<DownloadIcon />} onClick={() => {
                const downloadUrl = `${backendUrl}/gpt_researcher/local_files/${userId}/${file}`;
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = file;
                document.body.appendChild(link);
                link.click();
                link.remove();
              }} variant="solid" size="sm" colorScheme="blue">
                Download
              </Button>
              */}
              <IconButton
                            icon={<DeleteIcon />}
                            onClick={(evt) => deleteFile(file, evt)} // Pass the evt to the deleteFile function
                            aria-label="Delete file"
                            size="sm"
                            colorScheme="red"
                        />
            </HStack>
          ))
        )}
      </VStack>
    </Box>
  );
};

export default FileUploader;
